/**
 * An AI call campaign's call flow map (ring R8): the check the editor asks for
 * on every change, the ready-made maps, and what a problem is about.
 *
 * The map itself is an ordinary CRM flow map (`lib/flowMap.ts`, owner
 * "aiCampaign"); this file is the call side of it:
 *
 *   POST /api/admin/crm/calls/ai-outbound/flow/check      { doc } -> CallFlowCheck
 *   GET  /api/admin/crm/calls/ai-outbound/flow/templates          -> { templates }
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-calls/lib/outbound/flow/`
 * (`types.ts`, `templates.ts`); `aiCallFlow.test.ts` reads those files and holds
 * the lists and bounds below against them, so a problem code added on the server
 * fails a test here instead of rendering as a raw key.
 *
 * React-free: the marks and the problem subjects are the parts worth testing.
 */
import { readText, type FlowMapDoc, type FlowNodeMark } from "@wa/widgets/flow-map/model";
import { api } from "./api";

const BASE = "/admin/crm/calls/ai-outbound/flow";

/** Mirrors `FLOW_OUTCOMES`: what an end node may say the call came to. */
export const FLOW_OUTCOMES = [
  "meeting",
  "interested",
  "callback",
  "send_info",
  "send_report",
  "not_interested",
  "do_not_call",
  "wrong_person",
  "gatekeeper",
] as const;
export type FlowOutcome = (typeof FLOW_OUTCOMES)[number];

/** Mirrors `FLOW_PROBLEM_CODES`, in the server's order. */
export const FLOW_PROBLEM_CODES = [
  "no_start",
  "many_starts",
  "start_no_exit",
  "start_many_exits",
  "into_start",
  "step_many_exits",
  "dead_end",
  "decision_few_answers",
  "answer_unlabelled",
  "answer_twice",
  "end_exits",
  "end_no_outcome",
  "no_end",
  "unreachable",
  "no_way_to_end",
  "loop_without_question",
  "title_missing",
  "title_long",
  "text_long",
  "answer_long",
  "unknown_placeholder",
  "marker_in_text",
  "too_long",
] as const;
export type FlowProblemCode = (typeof FLOW_PROBLEM_CODES)[number];

/** Mirrors `FLOW_TEXT_LIMITS`. */
export const FLOW_TEXT_LIMITS = {
  title: 80,
  description: 300,
  answer: 60,
  course: 2500,
} as const;

/** Mirrors `CALL_FLOW_TEMPLATE_IDS`. */
export const CALL_FLOW_TEMPLATE_IDS = ["alert_report", "meeting", "qualify"] as const;
export type CallFlowTemplateId = (typeof CALL_FLOW_TEMPLATE_IDS)[number];

/** Mirrors `FlowProblem`. */
export interface CallFlowProblem {
  code: string;
  nodeId?: string;
  edgeId?: string;
  limit?: number;
  placeholder?: string;
}

/** The check's answer: `CompiledCallFlow` and the course's cap. */
export interface CallFlowCheck {
  ok: boolean;
  /** Nothing drawn but notes and groups: the campaign talks by its goal, which is not a fault. */
  empty: boolean;
  /** The course the AI would read; "" unless `ok`. */
  text: string;
  chars: number;
  limit: number;
  steps: Array<{ label: string; nodeId: string; kind: "step" | "decision" | "end"; outcome?: string }>;
  problems: CallFlowProblem[];
}

export interface CallFlowTemplate {
  id: CallFlowTemplateId;
  doc: FlowMapDoc;
}

export const checkCallFlow = (doc: FlowMapDoc) => api.post<CallFlowCheck>(`${BASE}/check`, { doc });
export const listCallFlowTemplates = () => api.get<{ templates: CallFlowTemplate[] }>(`${BASE}/templates`);

/** The catalogue suffix a problem code is worded under; a code this build does not know reads as "unknown". */
export function problemKey(code: string): FlowProblemCode | "unknown" {
  return (FLOW_PROBLEM_CODES as readonly string[]).includes(code) ? (code as FlowProblemCode) : "unknown";
}

/**
 * The nodes to draw red: every node a problem names, and for a problem about an
 * arrow, the node the arrow leaves (where the author fixes it). A problem about
 * the whole map marks nothing.
 */
export function problemMarks(problems: readonly CallFlowProblem[], doc: FlowMapDoc): Record<string, FlowNodeMark> {
  const nodes = new Set(doc.nodes.map((n) => n.id));
  const sources = new Map(doc.edges.map((e) => [e.id, e.source] as const));
  const marks: Record<string, FlowNodeMark> = {};
  for (const p of problems) {
    const id = p.nodeId ?? (p.edgeId ? sources.get(p.edgeId) : undefined);
    if (id && nodes.has(id)) marks[id] = "error";
  }
  return marks;
}

/** What a problem is about, as the list names it. */
export type ProblemSubject =
  | { kind: "map" }
  | { kind: "node"; title: string }
  | { kind: "arrow"; from: string; to: string };

/** The node or the arrow a problem is pinned to, by title; the whole map when it names neither or one that is gone. */
export function problemSubject(problem: CallFlowProblem, doc: FlowMapDoc, untitled: string): ProblemSubject {
  const title = (id: string): string | null => {
    const node = doc.nodes.find((n) => n.id === id);
    if (!node) return null;
    return readText(node.title).trim() || untitled;
  };
  if (problem.nodeId) {
    const t = title(problem.nodeId);
    return t === null ? { kind: "map" } : { kind: "node", title: t };
  }
  if (problem.edgeId) {
    const edge = doc.edges.find((e) => e.id === problem.edgeId);
    const from = edge ? title(edge.source) : null;
    const to = edge ? title(edge.target) : null;
    return from !== null && to !== null ? { kind: "arrow", from, to } : { kind: "map" };
  }
  return { kind: "map" };
}

/** Whether a check stands in the way of activating the campaign: something is drawn and it cannot be followed. */
export function flowBlocksActivation(check: Pick<CallFlowCheck, "ok" | "empty">): boolean {
  return !check.empty && !check.ok;
}

/**
 * Whether an alert campaign's map lacks an end that sends the defect report
 * (ring R9). Only an end node whose outcome is `send_report` produces that
 * result; without one every alert call ends as `send_info`, and a person has to
 * check the address by hand. Nothing drawn counts as missing. A broken map says
 * nothing here: its steps are unknown until it can be followed, and the check
 * panel already names what is wrong.
 */
export function flowMissesReportEnd(check: Pick<CallFlowCheck, "ok" | "empty" | "steps"> | null): boolean {
  if (!check) return false;
  if (check.empty) return true;
  if (!check.ok) return false;
  return !check.steps.some((step) => step.kind === "end" && step.outcome === "send_report");
}
